import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { UserCheck, LogIn, LogOut, SlidersHorizontal, Trophy, Megaphone } from "lucide-react";

function prevMonth() {
  const d = new Date(Date.now() + 7 * 3600 * 1000);
  d.setDate(1); d.setDate(0);
  return d.toISOString().slice(0, 7);
}
const monthName = (m) => new Date(m + "-01T00:00:00+07:00").toLocaleDateString("id-ID", { month: "long", year: "numeric", timeZone: "Asia/Jakarta" });

function ChampionCard({ isAdmin }) {
  const [champions, setChampions] = useState([]);
  const [champMonth, setChampMonth] = useState(prevMonth());
  const [busy, setBusy] = useState(false);
  const [previewFor, setPreviewFor] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = () => api.get("/champions").then(({ data }) => setChampions(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const prepare = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/champions/prepare", { month: champMonth });
      if (data.user_id) {
        toast.success(`Draf ${monthName(champMonth)} siap: ${data.full_name} (+${data.delta_total} poin). Cek rincian lalu ACC untuk umumkan.`);
        setPreviewFor(data.month);
        const p = await api.get(`/champions/preview?month=${data.month}`);
        setPreview(p.data);
      } else {
        toast.info(`Bulan ${monthName(champMonth)} tidak ada kenaikan poin — tidak ada kandidat`);
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menyiapkan"); }
    finally { setBusy(false); }
  };

  const recalc = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/champions/${champMonth}/recalculate`);
      if (data.user_id) {
        toast.success(`Draf ${monthName(champMonth)} dihitung ulang: ${data.full_name} (+${data.delta_total} poin, bonus Rp ${Number(data.bonus).toLocaleString("id-ID")})`);
        setPreviewFor(data.month);
        const p = await api.get(`/champions/preview?month=${data.month}`);
        setPreview(p.data);
      } else {
        toast.info(`Bulan ${monthName(champMonth)} tidak ada kenaikan poin — tidak ada kandidat`);
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menghitung ulang"); }
    finally { setBusy(false); }
  };

  const correctChamp = async (c) => {
    const t = toast.loading("Menghitung ulang & mengoreksi...");
    try {
      const { data } = await api.post(`/champions/${c.month}/recalculate`, { correct: true });
      if (data.corrected) toast.success(`Terkoreksi: bonus ${monthName(c.month)} kini Rp ${Number(data.bonus).toLocaleString("id-ID")} — selisih Rp ${Number(data.diff).toLocaleString("id-ID")} tercatat di pengeluaran`, { id: t });
      else toast.info("Sudah sesuai — tidak ada yang perlu dikoreksi", { id: t });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal koreksi", { id: t }); }
  };

  const acc = async (c) => {
    const t = toast.loading("Mengumumkan...");
    try {
      await api.post(`/champions/${c.id}/acc`);
      toast.success(`${c.full_name} resmi diumumkan ke semua akun — bonus Rp ${Number(c.bonus).toLocaleString("id-ID")} tercatat di pengeluaran`, { id: t });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal ACC", { id: t }); }
  };

  const togglePreview = async (month) => {
    if (previewFor === month) { setPreviewFor(null); setPreview(null); return; }
    setPreviewFor(month); setPreview(null);
    try {
      const { data } = await api.get(`/champions/preview?month=${month}`);
      setPreview(data);
    } catch { setPreview({ error: true }); }
  };

  return (
    <div data-testid="champion-card" className="bg-white rounded-xl border border-amber-300 p-5 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-amber-400 text-white flex items-center justify-center"><Trophy size={18} /></div>
        <div>
          <h2 className="font-bold text-lg text-slate-900" style={{fontFamily:'Manrope'}}>Karyawan Terbaik Bulanan</h2>
          <p className="text-xs text-slate-500">Sistem menyiapkan kandidat tiap tanggal 1 — cek rincian kenaikan poin, lalu ACC untuk mengumumkan ke semua akun. Bonus Rp 250.000 untuk poin terbanyak sebulan; jika orang yang sama menang lagi bulan berikutnya naik Rp 100.000 terus (250rb → 350rb → 450rb); jika orang lain yang menang, bonus kembali Rp 250.000.</p>
        </div>
      </div>
      {isAdmin && (
        <div className="flex gap-2">
          <input data-testid="champion-month" type="month" value={champMonth} max={prevMonth()} onChange={(e) => setChampMonth(e.target.value)} className="h-11 px-3 rounded-lg border border-slate-300 bg-white text-sm" />
          <button data-testid="champion-prepare" onClick={prepare} disabled={busy} className="h-11 px-4 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-bold flex items-center gap-2 active:scale-[0.98] transition-transform">
            <Megaphone size={15} /> Hitung Kandidat
          </button>
          <button data-testid="champion-recalc" onClick={recalc} disabled={busy} className="h-11 px-4 rounded-lg border border-amber-500 text-amber-700 disabled:opacity-50 text-sm font-bold active:scale-[0.98] transition-transform">Hitung Ulang</button>
        </div>
      )}
      <div className="space-y-2">
        {champions.length === 0 && <p className="text-sm text-slate-500">Belum ada pemenang yang diumumkan. Kandidat dihitung otomatis tiap tanggal 1, atau siapkan manual di atas.</p>}
        {champions.map((c) => (
          <div key={c.id} data-testid={`champion-${c.month}`} className="bg-amber-50 rounded-lg border border-amber-200 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate">{c.full_name || "Tidak ada pemenang"}</p>
                <p className="text-xs text-slate-500">{monthName(c.month)}{c.delta_total ? ` · +${c.delta_total} poin` : ""}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {c.status === "draft" && isAdmin ? (
                  <>
                    <button data-testid={`champion-preview-${c.month}`} onClick={() => togglePreview(c.month)} className="h-9 px-3 rounded-lg border border-amber-400 text-amber-800 text-xs font-bold">Rincian</button>
                    {c.user_id && <button data-testid={`champion-acc-${c.month}`} onClick={() => acc(c)} className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold">ACC & Umumkan</button>}
                  </>
                ) : c.bonus > 0 ? (
                  <div className="text-right">
                    <p className="text-sm font-extrabold text-emerald-700" style={{fontFamily:'Manrope'}}>Rp {Number(c.bonus).toLocaleString("id-ID")}</p>
                    {c.streak > 1 && <p className="text-[10px] font-bold text-amber-700 uppercase">Bertahan {c.streak} bln</p>}
                    {c.status === "announced" && isAdmin && (
                      <button data-testid={`champion-correct-${c.month}`} onClick={() => correctChamp(c)} className="mt-0.5 text-[10px] font-bold text-amber-700 underline">Koreksi</button>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
            {c.status === "draft" && isAdmin && <span className="mt-1 inline-block px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-wider">Menunggu ACC</span>}
            {previewFor === c.month && (
              <div data-testid={`champion-ranking-${c.month}`} className="mt-2 bg-white rounded-lg border border-amber-200 p-3 space-y-1.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Peringkat kenaikan poin {monthName(c.month)}</p>
                {preview === null && <p className="text-xs text-slate-500">Memuat rincian...</p>}
                {preview?.error && <p className="text-xs text-red-600">Gagal memuat rincian.</p>}
                {preview && !preview.error && preview.ranking.length === 0 && <p className="text-xs text-slate-500">Tidak ada perubahan poin bulan ini.</p>}
                {preview && !preview.error && preview.ranking.map((r, i) => (
                  <div key={r.user_id} data-testid={`rank-${i}`} className={`text-xs rounded-lg p-2 ${i === 0 && r.delta_total > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
                    <div className="flex justify-between gap-2">
                      <span className="font-bold text-slate-800">#{i + 1} {r.full_name}</span>
                      <span className={`font-bold ${r.delta_total > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{r.delta_total > 0 ? '+' : ''}{r.delta_total} poin</span>
                    </div>
                    {r.reasons.map((h, j) => (
                      <p key={j} className="mt-0.5 text-slate-500 pl-5">
                        <span className={`font-bold ${h.delta > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{h.delta > 0 ? '+' : ''}{h.delta}</span> {h.reason} <span className="text-slate-400">· {h.by}</span>
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Kinerja() {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [adjustFor, setAdjustFor] = useState(null);
  const [delta, setDelta] = useState("1");
  const [reason, setReason] = useState("");
  const [effDate, setEffDate] = useState("");
  const [reasonsFor, setReasonsFor] = useState(null);

  const load = async () => {
    setError("");
    try {
      setData((await api.get(`/attendance/summary?month=${month}`)).data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Gagal memuat data kinerja");
    }
  };
  useEffect(() => { load(); }, [month]);

  const submitAdjust = async (uid) => {
    const d = parseInt(delta);
    if (!d) return toast.error("Delta tidak valid (contoh: 5 atau -3)");
    try {
      const res = await api.post("/scores/adjust", { user_id: uid, delta: d, reason, effective_date: effDate || undefined });
      toast.success(`Score diperbarui — dihitung untuk bulan ${res.data.month}`);
      if (res.data.champion_announced) toast.warning(`Juara bulan ${res.data.month} sudah diumumkan & bonus sudah dibayar — pemenang tidak berubah, poin tetap tercatat.`);
      else if (res.data.champion_draft_refreshed) toast.info(`Draf juara bulan ${res.data.month} dihitung ulang mengikuti poin ini.`);
      setAdjustFor(null); setReason(""); setDelta("1"); setEffDate("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  const scoreColor = (s) => s >= 100 ? 'text-emerald-600' : s >= 80 ? 'text-amber-600' : 'text-red-600';
  const barColor = (s) => s >= 100 ? 'bg-emerald-500' : s >= 80 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Kinerja & Score</p>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Kinerja Karyawan</h1>
        <p className="text-sm text-slate-600 mt-1">Total kehadiran, keterlambatan, pulang lebih awal, dan score prestasi per karyawan. Score mulai dari 100 — telat otomatis -2, pulang lebih awal -1.</p>
      </div>

      <label className="block max-w-xs">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Bulan</span>
        <input data-testid="kinerja-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base" />
      </label>

      <ChampionCard isAdmin={user?.role === "admin"} />

      <div className="space-y-3">
        {error && <p data-testid="kinerja-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</p>}
        {data && data.rows.length === 0 && <p className="text-sm text-slate-500">Belum ada karyawan aktif.</p>}
        {data?.rows.map((r) => (
          <div key={r.user_id} data-testid={`kinerja-${r.user_id}`} className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-slate-900 truncate" style={{fontFamily:'Manrope'}}>{r.full_name}</p>
                <p className="text-xs text-slate-500 capitalize">{r.role}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`text-2xl font-extrabold ${scoreColor(r.score)}`} style={{fontFamily:'Manrope'}}>{r.score}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Score</p>
              </div>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${barColor(r.score)} transition-all`} style={{ width: `${Math.min(100, (r.score / 150) * 100)}%` }} />
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-slate-50 rounded-lg p-2">
                <UserCheck size={14} className="mx-auto text-slate-400" />
                <p className="text-lg font-bold text-slate-900" style={{fontFamily:'Manrope'}}>{r.days}</p>
                <p className="text-[10px] text-slate-500 uppercase font-bold">Hadir</p>
              </div>
              <div className={`rounded-lg p-2 ${r.late_count ? 'bg-red-50' : 'bg-slate-50'}`}>
                <LogIn size={14} className="mx-auto text-slate-400" />
                <p className="text-lg font-bold text-slate-900" style={{fontFamily:'Manrope'}}>{r.late_count}<span className="text-xs font-normal text-slate-500"> / {r.late_minutes} mnt</span></p>
                <p className="text-[10px] text-slate-500 uppercase font-bold">Telat</p>
              </div>
              <div className={`rounded-lg p-2 ${r.early_count ? 'bg-red-50' : 'bg-slate-50'}`}>
                <LogOut size={14} className="mx-auto text-slate-400" />
                <p className="text-lg font-bold text-slate-900" style={{fontFamily:'Manrope'}}>{r.early_count}<span className="text-xs font-normal text-slate-500"> / {r.early_minutes} mnt</span></p>
                <p className="text-[10px] text-slate-500 uppercase font-bold">Plg Awal</p>
              </div>
            </div>
            {r.delta_month !== 0 && (
              <div>
                <button data-testid={`delta-month-${r.user_id}`} onClick={() => setReasonsFor(reasonsFor === r.user_id ? null : r.user_id)} className={`text-xs font-bold px-2.5 py-1 rounded-full ${r.delta_month > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {r.delta_month > 0 ? `+${r.delta_month}` : r.delta_month} poin bulan ini — {reasonsFor === r.user_id ? "tutup rincian" : "lihat rincian"}
                </button>
                {reasonsFor === r.user_id && (
                  <div data-testid={`reasons-${r.user_id}`} className="mt-2 space-y-1.5 bg-slate-50 rounded-lg p-3">
                    {(r.score_reasons || []).map((h, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <span className={`px-1.5 py-0.5 rounded font-bold flex-shrink-0 ${h.delta > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{h.delta > 0 ? '+' : ''}{h.delta}</span>
                        <div className="min-w-0">
                          <p className="text-slate-700 font-semibold">{h.reason}</p>
                          <p className="text-slate-400">oleh {h.by} · {new Date(h.at).toLocaleString('id-ID')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {user?.role === "admin" && (
              adjustFor === r.user_id ? (
                <div className="border-t border-slate-100 pt-3 space-y-2">
                  <div className="flex gap-2">
                    <input data-testid={`delta-${r.user_id}`} type="number" value={delta} onChange={(e) => setDelta(e.target.value)} className="w-24 h-11 px-3 rounded-lg border border-slate-300 text-sm" placeholder="+5 / -3" />
                    <input data-testid={`reason-${r.user_id}`} type="text" value={reason} onChange={(e) => setReason(e.target.value)} className="flex-1 h-11 px-3 rounded-lg border border-slate-300 text-sm min-w-0" placeholder="Alasan (wajib)" />
                  </div>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tanggal kejadian (opsional — isi tanggal bulan lalu agar poin dihitung bulan itu)</span>
                    <input data-testid={`effdate-${r.user_id}`} type="date" max={new Date().toISOString().slice(0, 10)} value={effDate} onChange={(e) => setEffDate(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-300 text-sm" />
                  </label>
                  <div className="flex gap-2">
                    <button data-testid={`submit-adjust-${r.user_id}`} onClick={() => submitAdjust(r.user_id)} className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-sm active:scale-[0.98] transition-transform">Simpan Penyesuaian</button>
                    <button data-testid={`abort-adjust-${r.user_id}`} onClick={() => setAdjustFor(null)} className="h-11 px-4 rounded-lg border border-slate-300 text-sm font-bold text-slate-600">Batal</button>
                  </div>
                </div>
              ) : (
                <button data-testid={`adjust-${r.user_id}`} onClick={() => { setAdjustFor(r.user_id); setDelta("1"); setReason(""); setEffDate(""); }} className="w-full h-11 border border-slate-200 rounded-lg text-sm font-bold text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
                  <SlidersHorizontal size={14} /> Sesuaikan Score
                </button>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
